import { createFileRoute, useParams, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { AppShell } from "@/components/app-shell";
import { requireRole } from "@/lib/session";
import { useTeacherContext } from "@/lib/teacher-context";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { VideoRoom, type VideoRoomHandle } from "@/components/live/video-room";
import { videoService } from "@/lib/video/service";
import { RecordingUploader } from "@/components/live/recording-uploader";
import { AiAssistantPanel } from "@/components/live/ai-assistant-panel";

import {
  generateLiveWrapUp,
  saveLiveWrapUp,
  publishHomeworkAsAssignment,
} from "@/lib/live-lesson-ai.functions";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  ArrowLeft, Users, MicOff, Lock, Captions, Hand, Grid3x3, PhoneOff,
  Sparkles, NotebookPen, ClipboardCheck, Save, ListChecks,
} from "lucide-react";


export const Route = createFileRoute("/teacher/live/$id")({
  ssr: false,
  beforeLoad: () => requireRole("teacher"),
  head: () => ({ meta: [{ title: "Live · MAKAI" }] }),
  component: TeacherLiveRoom,
});

type Lesson = {
  title: string; room_name: string; status: string;
  school_id: string; stream_id: string; subject_id: string | null;
  recording_resource_id: string | null; recording_storage_path: string | null;
  ai_summary: string | null; ai_notes: string | null; ai_key_points: unknown;
  homework: string | null; homework_assignment_id: string | null;
  host_notes: string | null;
  provider: string | null;
};

function TeacherLiveRoom() {
  const { id } = useParams({ from: "/teacher/live/$id" });
  const ctx = useTeacherContext();
  const [lesson, setLesson] = useState<Lesson | null>(null);
  const [attendance, setAttendance] = useState<{ user_id: string; joined_at: string; left_at: string | null; learner_id: string | null }[]>([]);
  const [names, setNames] = useState<Record<string, string>>({});
  const [hostNotes, setHostNotes] = useState("");
  const [summary, setSummary] = useState("");
  const [notes, setNotes] = useState("");
  const [homework, setHomework] = useState("");
  const [dueDate, setDueDate] = useState(() => new Date(Date.now() + 3 * 86400_000).toISOString().slice(0, 10));
  const [genBusy, setGenBusy] = useState(false);
  const [pubBusy, setPubBusy] = useState(false);
  const [lobbyOn, setLobbyOn] = useState(false);
  const attendanceRowId = useRef<string | null>(null);
  const roomRef = useRef<VideoRoomHandle>(null);

  const genWrap = useServerFn(generateLiveWrapUp);
  const saveWrap = useServerFn(saveLiveWrapUp);
  const publishHw = useServerFn(publishHomeworkAsAssignment);

  useEffect(() => {
    (async () => {
      const { data } = await supabase.from("live_lessons")
        .select("title, room_name, status, school_id, stream_id, subject_id, recording_resource_id, recording_storage_path, provider, ai_summary, ai_notes, ai_key_points, homework, homework_assignment_id, host_notes")
        .eq("id", id).maybeSingle();
      if (data) {
        setLesson(data as Lesson);
        setHostNotes(data.host_notes ?? "");
        setSummary(data.ai_summary ?? "");
        setNotes(data.ai_notes ?? "");
        setHomework(data.homework ?? "");
      }
    })();
  }, [id]);

  async function loadAttendance() {
    const { data } = await supabase.from("live_lesson_attendance")
      .select("user_id, joined_at, left_at, learner_id").eq("live_lesson_id", id)
      .order("joined_at", { ascending: false });
    const rows = (data ?? []) as typeof attendance;
    setAttendance(rows);
    const learnerIds = rows.map((r) => r.learner_id).filter(Boolean) as string[];
    if (learnerIds.length) {
      const { data: ls } = await supabase.from("learners").select("id, first_name, last_name").in("id", learnerIds);
      const map: Record<string, string> = {};
      for (const l of ls ?? []) map[l.id] = `${l.first_name} ${l.last_name}`.trim();
      setNames(map);
    }
  }
  useEffect(() => { loadAttendance(); const t = setInterval(loadAttendance, 15_000); return () => clearInterval(t); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  async function onJoined() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const { data } = await supabase.from("live_lesson_attendance").insert({
      live_lesson_id: id, user_id: user.id, role: "teacher",
    }).select("id").single();
    attendanceRowId.current = data?.id ?? null;
    await supabase.from("live_lessons").update({ status: "live", actual_start: new Date().toISOString() }).eq("id", id);
  }
  async function onLeft() {
    if (attendanceRowId.current) {
      await supabase.from("live_lesson_attendance")
        .update({ left_at: new Date().toISOString() })
        .eq("id", attendanceRowId.current);
    }
    await supabase.from("live_lessons").update({ status: "ended", actual_end: new Date().toISOString() }).eq("id", id);
    setLesson((l) => l ? { ...l, status: "ended" } : l);
  }

  async function handleGenerate() {
    try {
      setGenBusy(true);
      const r = await genWrap({ data: { liveLessonId: id, hostNotes: hostNotes || undefined } });
      setSummary(r.summary);
      setNotes(r.notes);
      setHomework(r.homework);
      toast.success("AI wrap-up ready — review and tweak before publishing.");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "AI failed");
    } finally {
      setGenBusy(false);
    }
  }

  async function handleSave() {
    try {
      await saveWrap({ data: { liveLessonId: id, summary, notes, homework, hostNotes } });
      toast.success("Saved.");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Save failed");
    }
  }

  async function handlePublish() {
    try {
      setPubBusy(true);
      await saveWrap({ data: { liveLessonId: id, summary, notes, homework, hostNotes } });
      const r = await publishHw({ data: { liveLessonId: id, dueDate, maxPoints: 20 } });
      toast.success(r.alreadyPublished ? "Homework already published." : "Homework published to learners.");
      setLesson((l) => l ? { ...l, homework_assignment_id: r.assignmentId } : l);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Publish failed");
    } finally {
      setPubBusy(false);
    }
  }

  const ended = lesson?.status === "ended";

  return (
    <AppShell role="teacher" title={lesson?.title ?? "Live lesson"} subtitle="You are the host"
      action={<Button asChild variant="ghost" size="sm"><Link to="/teacher/live"><ArrowLeft className="mr-1 h-4 w-4" /> Back</Link></Button>}>

      {lesson?.room_name && ctx.fullName && (
        <VideoRoom ref={roomRef} provider={lesson.provider} roomName={lesson.room_name} displayName={ctx.fullName} isModerator onJoined={onJoined} onLeft={onLeft} />
      )}

      {/* Teacher controls */}
      <Card className="mt-4 p-3">
        <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Teacher controls</div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" className="gap-1" onClick={() => roomRef.current?.muteEveryone()}>
            <MicOff className="h-4 w-4" /> Mute all
          </Button>
          <Button size="sm" variant="outline" className="gap-1" onClick={() => { const next = !lobbyOn; setLobbyOn(next); roomRef.current?.toggleLobby(next); }}>
            <Lock className="h-4 w-4" /> {lobbyOn ? "Unlock room" : "Lock room"}
          </Button>
          <Button size="sm" variant="outline" className="gap-1" onClick={() => roomRef.current?.toggleCaptions()}>
            <Captions className="h-4 w-4" /> Captions
          </Button>
          <Button size="sm" variant="outline" className="gap-1" onClick={() => roomRef.current?.toggleRaiseHand()}>
            <Hand className="h-4 w-4" /> Raise hand
          </Button>
          <Button size="sm" variant="outline" className="gap-1" onClick={() => roomRef.current?.toggleTileView()}>
            <Grid3x3 className="h-4 w-4" /> Tile view
          </Button>
          <Button size="sm" variant="destructive" className="gap-1" onClick={() => roomRef.current?.endMeetingForAll()}>
            <PhoneOff className="h-4 w-4" /> End for all
          </Button>
        </div>
      </Card>

      <div className="mt-4 grid gap-4 lg:grid-cols-[1fr_320px]">
        {/* AI wrap-up */}
        <Card className="p-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h3 className="inline-flex items-center gap-2 font-display text-lg font-bold">
              <Sparkles className="h-5 w-5 text-primary" /> AI wrap-up
            </h3>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" onClick={handleGenerate} disabled={genBusy} className="gap-1">
                <Sparkles className="h-4 w-4" /> {genBusy ? "Thinking…" : "Generate with MAKAI"}
              </Button>
              <Button size="sm" variant="outline" onClick={handleSave} className="gap-1">
                <Save className="h-4 w-4" /> Save
              </Button>
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <Label className="text-xs">In-class notes (helps the AI)</Label>
              <Textarea rows={2} value={hostNotes} onChange={(e) => setHostNotes(e.target.value)} placeholder="What did you focus on? Any sticky points?" />
            </div>
            <div>
              <Label className="text-xs inline-flex items-center gap-1"><NotebookPen className="h-3.5 w-3.5" /> Summary</Label>
              <Textarea rows={3} value={summary} onChange={(e) => setSummary(e.target.value)} placeholder="3–5 sentence summary parents will read." />
            </div>
            <div>
              <Label className="text-xs">Lesson notes (markdown)</Label>
              <Textarea rows={8} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Structured notes for learners." />
            </div>
            <div>
              <Label className="text-xs inline-flex items-center gap-1"><ClipboardCheck className="h-3.5 w-3.5" /> Homework brief</Label>
              <Textarea rows={4} value={homework} onChange={(e) => setHomework(e.target.value)} placeholder="What learners must do at home." />
            </div>

            <div className="flex flex-wrap items-end gap-3 rounded-lg border border-dashed border-border bg-muted/40 p-3">
              <div>
                <Label className="text-xs">Due date</Label>
                <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
              </div>
              <Button onClick={handlePublish} disabled={pubBusy || !homework} className="gap-1">
                <ClipboardCheck className="h-4 w-4" /> {lesson?.homework_assignment_id ? "Re-publish homework" : "Publish homework as assignment"}
              </Button>
              {lesson?.homework_assignment_id && (
                <span className="text-xs text-muted-foreground">Already linked to an assignment.</span>
              )}
            </div>

            {lesson?.homework_assignment_id && (
              <SubmissionsPanel assignmentId={lesson.homework_assignment_id} streamId={lesson.stream_id} />
            )}
          </div>
        </Card>

        <div className="lg:col-span-2">
          <AiAssistantPanel liveLessonId={id} hostNotes={hostNotes} />
        </div>




        {/* Attendance */}
        <Card className="p-4">
          <h3 className="mb-3 inline-flex items-center gap-2 font-semibold"><Users className="h-4 w-4" /> Attendance ({attendance.length})</h3>
          {attendance.length === 0 ? (
            <p className="text-sm text-muted-foreground">Learners will appear here as they join.</p>
          ) : (
            <ul className="divide-y divide-border/60 text-sm">
              {attendance.map((a, i) => (
                <li key={i} className="flex items-center justify-between gap-2 py-2">
                  <span className="truncate">{a.learner_id ? names[a.learner_id] ?? "Learner" : "Guest"}</span>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {new Date(a.joined_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    {a.left_at && ` → ${new Date(a.left_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {lesson && ended && (
        <div className="mt-4">
          <RecordingUploader
            liveLessonId={id}
            schoolId={lesson.school_id}
            streamId={lesson.stream_id}
            subjectId={lesson.subject_id}
            title={lesson.title}
            existingResourceId={lesson.recording_resource_id}
            existingPath={lesson.recording_storage_path}
            onPublished={(rid) => setLesson((l) => l ? { ...l, recording_resource_id: rid } : l)}
          />
        </div>
      )}
    </AppShell>
  );
}

function SubmissionsPanel({ assignmentId, streamId }: { assignmentId: string; streamId: string }) {
  const [stats, setStats] = useState({ enrolled: 0, submitted: 0, graded: 0 });

  async function load() {
    const [{ count: enrolled }, { data: subs }] = await Promise.all([
      supabase.from("learners").select("id", { count: "exact", head: true })
        .eq("current_stream_id", streamId).eq("status", "active"),
      supabase.from("assignment_submissions").select("status").eq("assignment_id", assignmentId),
    ]);
    const rows = subs ?? [];
    setStats({
      enrolled: enrolled ?? 0,
      submitted: rows.filter((r) => r.status === "submitted" || r.status === "late" || r.status === "graded").length,
      graded: rows.filter((r) => r.status === "graded").length,
    });
  }
  useEffect(() => { load(); const t = setInterval(load, 20_000); return () => clearInterval(t); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [assignmentId, streamId]);

  const pct = stats.enrolled ? Math.round((stats.submitted / stats.enrolled) * 100) : 0;

  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="inline-flex items-center gap-2 text-sm font-semibold">
          <ListChecks className="h-4 w-4 text-primary" /> Homework submissions
        </div>
        <Button asChild size="sm" className="gap-1">
          <Link to="/teacher/assignments" search={{ open: assignmentId }}>
            Review & mark <ClipboardCheck className="h-4 w-4" />
          </Link>
        </Button>
      </div>
      <div className="grid grid-cols-3 gap-2 text-center text-xs">
        <div className="rounded-md bg-muted/50 p-2">
          <div className="font-display text-lg font-bold">{stats.submitted}<span className="text-muted-foreground">/{stats.enrolled}</span></div>
          <div className="text-muted-foreground">Turned in</div>
        </div>
        <div className="rounded-md bg-muted/50 p-2">
          <div className="font-display text-lg font-bold">{stats.graded}</div>
          <div className="text-muted-foreground">Marked</div>
        </div>
        <div className="rounded-md bg-muted/50 p-2">
          <div className="font-display text-lg font-bold">{Math.max(0, stats.submitted - stats.graded)}</div>
          <div className="text-muted-foreground">To mark</div>
        </div>
      </div>
      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
        <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

